import React, { FC, useMemo } from 'react';
import { Category, DemandArea } from '../../../../lib/types/endUser';
import { CoreVisualSectionT } from '../../../../lib/types/sections';
import { useAppDispatch, useAppSelector } from '../../../../lib/store/hooks';
import { useNavigate } from 'react-router-dom';
import { sendDataToAnalytics } from '../../../../lib/utils/sendDataToAnalytics';
import { setCurrentCategory } from '../../../../lib/store/slices/endUserSlice';

import styles from './index.module.scss';
import { EndUserTypography } from '../../../../components/EndUserTypography';
import { FinderButton } from '../../../../components/FinderButton';
import { EndUserSquareTiles } from '../EndUserSquareTiles';
import { ROUTER_PATHS } from '../../../../lib/utils/router-paths';
import { END_USER_FRENCH_TENANT_MODEL_NAME } from '../../../../lib/utils/constants';
import { ApplyCampaignBadgeT } from '../../../../lib/hooks/endUserHooks/useApplyCampaignHouseBadge';
import { Button } from 'antd';
import { useGetCampaignObjectPositions } from '../../../../lib/hooks/endUserHooks/useGetCampaignObjectPositions';
import {
  CAMPAIGN_POSITION_ELEMENT_OPTIONS,
  CAMPAIGN_POSITION_PAGES_OPTIONS,
  CAMPAIGN_POSITION_SECTION_OPTIONS,
} from '../../../campaignDetails/hooks/useGetPositionOption';
import { CampaignObjectImage } from '../../../../components/CampaignObjectImage';

type Props = {
  demandAreas?: DemandArea[];
  content?: CoreVisualSectionT;
  applyCampaignHouseBadge: ApplyCampaignBadgeT;
};

export const EndUserDemandAreaTilesDesktop: FC<Props> = ({
  demandAreas,
  content,
  applyCampaignHouseBadge,
}) => {
  const dispatch = useAppDispatch();
  const { demandArea } = useAppSelector(({ endUser }) => endUser);
  const isFrenchTenant = demandArea?.project?.name === END_USER_FRENCH_TENANT_MODEL_NAME;

  const { foundObjects } = useGetCampaignObjectPositions({
    page: CAMPAIGN_POSITION_PAGES_OPTIONS.LANDING_PAGE,
    sections: [CAMPAIGN_POSITION_SECTION_OPTIONS.HOUSE],
    elements: [CAMPAIGN_POSITION_ELEMENT_OPTIONS.HEADLINE],
  });

  const headlineCampaignObject = useMemo(() => {
    if (foundObjects.length > 0) {
      return foundObjects[0];
    }
  }, [foundObjects]);

  const navigate = useNavigate();

  const handleAreaClick = (areaId: number, name: string) => {
    sendDataToAnalytics(
      'Bedarfsbereich',
      `Aufruf Bedarfsbereich ${name}`,
      `User exploriert Produkte aus Bedarfsbereich ${name}`,
    );

    const path = isFrenchTenant ? ROUTER_PATHS.houseModelFr : ROUTER_PATHS.houseModel;

    navigate(`/${path}/${areaId}`);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    areaId: number,
    name: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleAreaClick(areaId, name);
    }
  };

  const handleCategoryClick = (areaId: number, category: Category) => {
    setTimeout(() => dispatch(setCurrentCategory({ category })), 10);
  };

  const RenderBadge = useMemo(() => {
    if (applyCampaignHouseBadge.isBadgeEnabled) {
      return (
        <Button
          onClick={applyCampaignHouseBadge.onScrollToTeaser}
          className={styles.campaignBadge}
        >
          <img alt="badge" src={applyCampaignHouseBadge.badgeImageUrl} />
        </Button>
      );
    }
  }, [
    applyCampaignHouseBadge.badgeImageUrl,
    applyCampaignHouseBadge.isBadgeEnabled,
    applyCampaignHouseBadge.onScrollToTeaser,
  ]);

  return (
    <div
      className={styles.section}
      style={{
        background: `linear-gradient(89.55deg, rgba(0, 0, 0, 0.15) 3.66%, rgba(0, 0, 0, 0.09) 32.16%, rgba(0, 0, 0, 0.06) 46.71%, rgba(0, 0, 0, 0.006) 99.64%), url(${content?.backgroundImage?.s3Url})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left',
      }}
    >
      <div className={styles.headlineBlock}>
        <EndUserTypography
          type="headlineFirst"
          component="Title"
          className={styles.title}
        >
          {content?.headline1}

          {headlineCampaignObject && (
            <CampaignObjectImage
              campaignObject={headlineCampaignObject}
              containerClassName={styles.campaignElementContainer}
              completedIconClassName={styles.campaignElementCompletedIcon}
            />
          )}
        </EndUserTypography>
        <EndUserTypography
          type="headlineThird"
          component="Title"
          style={{ color: '#fff' }}
          className={styles.subTitle}
        >
          {content?.headline2}
        </EndUserTypography>

        <FinderButton
          showFinderButton={content?.finderEnabled}
          finderButtonText={content?.finderButtonText}
          finderButtonUrl={content?.finderButtonLink}
          recommendForAllCategories={true}
        />
      </div>

      <div className={styles.mainContainer}>
        {RenderBadge}
        <EndUserSquareTiles
          demandAreas={demandAreas}
          handleAreaClick={handleAreaClick}
          handleKeyDown={handleKeyDown}
          handleCategoryClick={handleCategoryClick}
        />
      </div>
    </div>
  );
};
